import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtTime } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import MobileHeader from "../../components/MobileHeader";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ArrowDown, ArrowUp, ShoppingBag, Wallet, RefreshCcw, Receipt, Smartphone } from "lucide-react";

const ICON = {
    disbursement: ArrowDown,
    topup: Wallet,
    send: ArrowUp,
    repayment: RefreshCcw,
    purchase: ShoppingBag,
    invoice: Receipt,
};

const ICON_COLOR = {
    disbursement: "#00D084",
    topup: "#0066FF",
    send: "#FF6B00",
    repayment: "#7000FF",
    purchase: "#FF0055",
    airtime: "#0066FF",
};

export default function Activity() {
    const { t } = useI18n();
    const [filter, setFilter] = useState("all");
    const [txns, setTxns] = useState([]);

    useEffect(() => {
        api.get("/wallet/transactions?limit=200").then((r) => setTxns(r.data));
    }, []);

    const filtered = txns.filter((tx) => {
        if (filter === "all") return true;
        if (filter === "credit") return ["disbursement", "topup"].includes(tx.category);
        if (filter === "wallet") return ["topup", "send"].includes(tx.category);
        if (filter === "payments") return ["repayment", "purchase"].includes(tx.category);
        return true;
    });

    // Group by day
    const groups = {};
    filtered.forEach((tx) => {
        const day = new Date(tx.created_at).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
        const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
        const yesterday = new Date(Date.now() - 86400000).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
        const label = day === today ? t("activity.today") : day === yesterday ? t("activity.yesterday") : day;
        groups[label] = groups[label] || [];
        groups[label].push(tx);
    });

    return (
        <div data-testid="activity-page">
            <MobileHeader title={t("activity.title")} subtitle={t("activity.subtitle")} backTo="/consumer" />
            <div className="px-5 -mt-6">
                <Tabs value={filter} onValueChange={setFilter}>
                    <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-4 w-full" data-testid="activity-tabs">
                        <TabsTrigger value="all" data-testid="filter-all">{t("activity.tab_all")}</TabsTrigger>
                        <TabsTrigger value="credit" data-testid="filter-credit">{t("activity.tab_credit")}</TabsTrigger>
                        <TabsTrigger value="wallet" data-testid="filter-wallet">{t("activity.tab_wallet")}</TabsTrigger>
                        <TabsTrigger value="payments" data-testid="filter-payments">{t("activity.tab_payments")}</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div className="px-5 mt-4 space-y-4 pb-4">
                {Object.keys(groups).length === 0 && (
                    <div className="text-center text-sm text-slate-500 py-12">{t("activity.empty")}</div>
                )}
                {Object.entries(groups).map(([day, items]) => (
                    <div key={day}>
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">{day}</div>
                        <div className="space-y-2">
                            {items.map((tx) => {
                                const Icon = ICON[tx.category] || Smartphone;
                                const color = ICON_COLOR[tx.category] || "#64748B";
                                return (
                                    <div key={tx.id} className="bg-white rounded-2xl p-4 border border-slate-200/60 flex items-center gap-3" data-testid={`activity-${tx.id}`}>
                                        <div className="size-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15`, color }}>
                                            <Icon size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm truncate">{tx.description}</div>
                                            <div className="text-xs text-slate-500">{fmtTime(tx.created_at)}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className={`font-display font-bold text-sm ${tx.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                                                {tx.type === "credit" ? "+" : "−"} {fmtCurrency(tx.amount)}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-wider text-emerald-500 font-bold">{t("activity.success")}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
